import { useState } from "react";
import TreeDataGrid from "../DataGrids/TreeDataGrid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  UncontrolledTooltip,
} from "reactstrap";
import { FormattedMessage } from "react-intl";

export default function ResultTable(props) {
  const [showStatusInfoModal, setShowStatusInfoModal] = useState(false);
  const [showNbrOfAttemptsInfoModal, setNbrOfAttemptsInfoModal] =
    useState(false);
  const [showFocusControlInfoModal, setFocusControlInfoModal] = useState(false);

  const rightAlignedCols = props.rightAlignedColumns || [];
  const centerAlignedCols = props.centerAlignedColumns || [];

  const statusPriority = {
    InProgress: 1,
    NotStarted: 0,
    Done: 2,
    Completed: 3,
  };

  const comparePriority = (rowA, rowB, columnId) => {
    const a = rowA.original[columnId];
    const b = rowB.original[columnId];
    if ((!a || !a.props) && (!b || !b.props)) return 0;
    if (!a || !a.props) return -1;
    if (!b || !b.props) return 1;
    const priorityA = statusPriority[a.props.status] ?? -1;
    const priorityB = statusPriority[b.props.status] ?? -1;
    return priorityA - priorityB;
  };

  const comparePercentage = (rowA, rowB, columnId) => {
    const a = rowA.original[columnId];
    const b = rowB.original[columnId];
    if (!a && !b) return 0;
    if (!a) return -1;
    if (!b) return 1;

    const getPriority = (value) => {
      if (value === 0) return 0;
      const percentageArray = String(value).match(/\(\d+%\)/);
      if (!percentageArray) return Number(value);
      const percentage = percentageArray[0].replace(/[()%]/g, "");
      return Number(percentage);
    };

    return getPriority(a) - getPriority(b);
  };

  const getCellClassName = (columnName) => {
    if (rightAlignedCols.includes(columnName)) return "right-align-cell";
    if (centerAlignedCols.includes(columnName)) return "center-align-cell";
    return "";
  };

  const getHeaderClassName = (columnName) => {
    if (rightAlignedCols.includes(columnName)) return "right-align-header";
    if (centerAlignedCols.includes(columnName)) return "center-align-header";
    return "";
  };

  const infoIconColumns = {
    status: {
      iconId: "statusInfoIcon",
      iconClass: "status-info-icon text-primary",
      onClick: () => setShowStatusInfoModal(true),
      justify: "justify-content-center",
    },
    nbrOfAttempts: {
      iconId: "nbrOfAttemptsInfoIcon",
      iconClass: "attempts-info-icon text-primary",
      onClick: () => setNbrOfAttemptsInfoModal(true),
      justify: "justify-content-end",
      tooltip: <FormattedMessage id="NbrOfAttempts_Info" />,
    },
    focusLostCount: {
      iconId: "focusControlInfoIcon",
      iconClass: "focus-control-info-icon text-primary",
      onClick: () => setFocusControlInfoModal(true),
      justify: "justify-content-end",
    },
  };

  const sortingFnMap = {
    status: comparePriority,
    score: comparePercentage,
    activityPoints: comparePercentage,
  };

  const sortingDisabled = ["showResult", "lastRun"];

  const columns = props.columns.map((col) => {
    const ext = props.columnExtensions?.find((e) => e.columnName === col.name);
    const info = infoIconColumns[col.name];

    return {
      id: col.name,
      key: col.name,
      width: ext?.width ?? 150,
      sortable: !sortingDisabled.includes(col.name),
      sortingFn: sortingFnMap[col.name],
      header: info
        ? () => (
            <div className={`d-flex w-100 ${info.justify}`}>
              <div className="d-flex align-items-center">
                <span>{col.title}</span>
                <FontAwesomeIcon
                  id={info.iconId}
                  icon="circle-info"
                  className={info.iconClass}
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    info.onClick();
                  }}
                />
                {info.tooltip && (
                  <UncontrolledTooltip placement="top" target={info.iconId} fade={false}>
                    {info.tooltip}
                  </UncontrolledTooltip>
                )}
              </div>
            </div>
          )
        : col.title,
      cell: ({ getValue }) => getValue(),
      meta: {
        cellClassName: getCellClassName(col.name),
        headerClassName: getHeaderClassName(col.name),
      },
    };
  });

  const getRowClassName = (row) => {
    if (row.original.isGy25FinalExam) return "result-row--gy25FinalExam";
    return "";
  };

  return (
    <>
      <TreeDataGrid
        rows={props.tableData}
        columns={columns}
        treeColumnId="name"
        className="result-table"
        rowClassName={getRowClassName}
      />

      <Modal isOpen={showStatusInfoModal}>
        <ModalHeader className="justify-content-center py-2 px-2">
          <FormattedMessage id="StatusInfo_Title" />
        </ModalHeader>

        <ModalBody>
          <p>
            <FormattedMessage id="StatusInfo_Intro" />
          </p>

          <ul>
            <li>
              <FormattedMessage id="StatusInfo_LeftIcon" />
            </li>
            <li>
              <FormattedMessage id="StatusInfo_RightIcon" />
            </li>
          </ul>

          <p>
            <FormattedMessage id="StatusInfo_Observe" />
          </p>

          <p>
            <strong>
              <FormattedMessage id="StatusInfo_Statuses" />
            </strong>
          </p>

          <p>
            <strong>
              <FormattedMessage id="StatusInfo_NotStarted_Title" />
            </strong>
          </p>
          <ul>
            <li>
              <FormattedMessage id="StatusInfo_NotStarted_1" />
            </li>
            <li>
              <FormattedMessage id="StatusInfo_NotStarted_2" />
            </li>
            <li>
              <FormattedMessage id="StatusInfo_NotStarted_3" />
            </li>
          </ul>

          <p>
            <strong>
              <FormattedMessage id="StatusInfo_InProgress_Title" />
            </strong>
          </p>
          <ul>
            <li>
              <FormattedMessage id="StatusInfo_InProgress_1" />
            </li>
            <li>
              <FormattedMessage id="StatusInfo_InProgress_2" />
            </li>
            <li>
              <FormattedMessage id="StatusInfo_InProgress_3" />
            </li>
          </ul>

          <p>
            <strong>
              <FormattedMessage id="StatusInfo_Completed_Title" />
            </strong>
          </p>
          <ul>
            <li>
              <FormattedMessage id="StatusInfo_Completed_1" />
            </li>
            <li>
              <FormattedMessage id="StatusInfo_Completed_2" />
            </li>
            <li>
              <FormattedMessage id="StatusInfo_Completed_3" />
            </li>
          </ul>
        </ModalBody>

        <ModalFooter>
          <Button
            type="button"
            color="primary"
            outline
            onClick={() => setShowStatusInfoModal(false)}
          >
            <FormattedMessage id="Close" />
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={showNbrOfAttemptsInfoModal} size="sm" centered>
        <ModalBody>
          <p className="mb-0 fw-normal" style={{ fontSize: "0.9rem" }}>
            <FormattedMessage id="NbrOfAttempts_Info" />
          </p>
        </ModalBody>

        <ModalFooter>
          <Button
            type="button"
            color="primary"
            outline
            size="sm"
            onClick={() => setNbrOfAttemptsInfoModal(false)}
          >
            <FormattedMessage id="Close" />
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={showFocusControlInfoModal}
        toggle={() => setFocusControlInfoModal(false)}
        size="md"
        scrollable
      >
        <ModalHeader
          className="py-2 px-3"
          toggle={() => setFocusControlInfoModal(false)}
        >
          <FormattedMessage id="FocusControlInfo_Title" />
        </ModalHeader>

        <ModalBody className="px-3 py-3">
          <p className="text-muted mb-3">
            <FormattedMessage id="FocusControlInfo_Intro" />
          </p>

          <div className="bg-light rounded p-3 mb-3">
            <h6 className="fw-semibold mb-2">
              <FormattedMessage id="FocusControlInfo_Overview_Title" />
            </h6>
            <p className="mb-2">
              <FormattedMessage id="FocusControlInfo_Overview_Intro" />
            </p>
            <ul className="mb-2 ps-3">
              <li className="mb-1">
                <FormattedMessage id="FocusControlInfo_Overview_FocusLost" />
              </li>
              <li>
                <FormattedMessage id="FocusControlInfo_Overview_TotalTime" />
              </li>
            </ul>
            <p className="text-muted mb-0 small">
              <FormattedMessage id="FocusControlInfo_Overview_Outro" />
            </p>
          </div>

          <div className="bg-light rounded p-3">
            <h6 className="fw-semibold mb-2">
              <FormattedMessage id="FocusControlInfo_Detail_Title" />
            </h6>
            <p className="mb-2">
              <FormattedMessage id="FocusControlInfo_Detail_Intro" />
            </p>
            <p className="text-muted small mb-1">
              <FormattedMessage id="FocusControlInfo_Detail_Columns" />
            </p>
            <ul className="mb-2 ps-3">
              <li className="mb-1">
                <FormattedMessage id="FocusControlInfo_Detail_Attempt" />
                <span className="d-block text-muted small mt-1">
                  <FormattedMessage id="FocusControlInfo_Detail_Attempt_Extra" />
                </span>
              </li>
              <li className="mb-1">
                <FormattedMessage id="FocusControlInfo_Detail_Time" />
              </li>
              <li>
                <FormattedMessage id="FocusControlInfo_Detail_Duration" />
              </li>
            </ul>
            <p className="mb-0 text-muted small">
              <FormattedMessage id="FocusControlInfo_Detail_Outro" />
            </p>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            type="button"
            color="primary"
            outline
            onClick={() => setFocusControlInfoModal(false)}
          >
            <FormattedMessage id="Close" />
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
